import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import type { MaturityBreakdown } from '@/types';
import { Card, Heading, Chip, MonoLabel, InlineMessage } from '@/components/rc';

interface MaturityDonutProps {
  breakdown: MaturityBreakdown;
  onChipClick?: (value: string) => void;
}

const MATURITY_COLORS = {
  drinkSoon: 'var(--rc-maturity-drink-soon)',
  drink: 'var(--rc-maturity-drink)',
  drinkOrHold: 'var(--rc-maturity-drink-or-hold)',
  hold: 'var(--rc-maturity-hold)',
  pastPeak: 'var(--rc-maturity-past-peak)',
};

const MaturityDonut: React.FC<MaturityDonutProps> = ({ breakdown, onChipClick }) => {
  const hasData = breakdown.drink + breakdown.drinkOrHold + breakdown.drinkSoon + breakdown.hold + breakdown.pastPeak > 0;

  const data = [
    { name: 'Fading', value: breakdown.drinkSoon, color: MATURITY_COLORS.drinkSoon },
    { name: 'Peak', value: breakdown.drink, color: MATURITY_COLORS.drink },
    { name: 'Hold or Sip', value: breakdown.drinkOrHold, color: MATURITY_COLORS.drinkOrHold },
    { name: 'Ripening', value: breakdown.hold, color: MATURITY_COLORS.hold },
    { name: 'Tired', value: breakdown.pastPeak, color: MATURITY_COLORS.pastPeak },
  ].filter(d => d.value > 0);

  return (
    <Card elevation="flat" padding="standard" className="border border-[var(--rc-border-subtle)]">
      <Heading scale="subhead" className="mb-4 pb-2 border-b-4 border-[var(--rc-accent-acid)]">
        MATURITY BREAKDOWN
      </Heading>

      {!hasData ? (
        <InlineMessage tone="info" message="Add drinking windows to see maturity data" />
      ) : (
        <>
          <div className="relative h-48 sm:h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <PieChart>
                <Pie
                  data={data}
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <Heading scale="title" as="span" className="block">
                  {breakdown.total}
                </Heading>
                <MonoLabel size="micro" colour="ghost" className="block mt-1">
                  bottles
                </MonoLabel>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {breakdown.drinkSoon > 0 && (
              <Chip variant="Maturity" state="Selected" maturityValue="drink-soon" label={`Fading ${breakdown.drinkSoon}`} onClick={() => onChipClick?.('Fading')} />
            )}
            {breakdown.drink > 0 && (
              <Chip variant="Maturity" state="Selected" maturityValue="drink" label={`Peak ${breakdown.drink}`} onClick={() => onChipClick?.('Peak')} />
            )}
            {breakdown.drinkOrHold > 0 && (
              <Chip variant="Maturity" state="Selected" maturityValue="drink-or-hold" label={`Hold or Sip ${breakdown.drinkOrHold}`} onClick={() => onChipClick?.('Hold or Sip')} />
            )}
            {breakdown.hold > 0 && (
              <Chip variant="Maturity" state="Selected" maturityValue="hold" label={`Ripening ${breakdown.hold}`} onClick={() => onChipClick?.('Ripening')} />
            )}
            {breakdown.pastPeak > 0 && (
              <Chip variant="Maturity" state="Selected" maturityValue="past-peak" label={`Tired ${breakdown.pastPeak}`} onClick={() => onChipClick?.('Tired')} />
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default MaturityDonut;
